import type {RunOutcome} from './novaInvocationStore';

// Why this task cannot be actioned, as a property of the task and the person looking at it.
//
// 'no_edit_task' covers both halves of "may not act on this task": the person does not hold the
// edit-tasks permission, or they are a client user restricted from task actions. See
// deriveEligibilityFromTask for why those are one reason rather than two.
export type EligibilityReason = 'task_locked' | 'no_edit_task';

// A disabled control can also be blocked by run capacity rather than the task's own eligibility —
// once three tasks are running at once, every other idle control disables itself until one
// finishes. 'run_cap_reached' widens the disabled reason to cover that alongside eligibility.
//
// 'no_agent_available' is the third such reason: nothing about this task, and nothing about this
// person's permissions — there is simply no agent in the resolved list for a run to use. Its own
// member rather than anything phrased about the person, because no agent carries a role
// restriction: attributing an empty list to who the person is would state a cause that does not
// exist.
// 'out_of_credits' is the account's AI allowance being spent: nothing about this task or person,
// and it lifts by itself when the allowance resets or is topped up.
//
// 'last_run_failed' is the modal header's alone: the conversation's newest turn is an error nobody
// has retried or dismissed, so there is nothing to continue from until they do.
export type DisabledReason =
	| EligibilityReason
	| 'run_cap_reached'
	| 'no_agent_available'
	| 'out_of_credits'
	| 'last_run_failed';

// The message every surface gives for each reason. Each one names a cause the person can tell apart
// from the others: the task is locked, you may not edit tasks, Nova is busy with three already,
// there is no agent to run. Shared by the control and the in-task card beside it, which must never
// give two different reasons for the same refusal.
export const DISABLED_REASON_MESSAGE_ID: Record<DisabledReason, string> = {
	task_locked: 'nova_invocation.control.reason_task_locked',
	no_edit_task: 'nova_invocation.control.reason_no_edit_task',
	run_cap_reached: 'nova_invocation.control.reason_run_cap',
	no_agent_available: 'nova_invocation.control.reason_no_agent_available',
	out_of_credits: 'nova_invocation.control.reason_out_of_credits',
	last_run_failed: 'nova_invocation.control.reason_last_run_failed',
};

export type ControlState =
	| {kind: 'action'}
	| {kind: 'working'}
	| {kind: 'ready_or_continue'; read: boolean}
	| {kind: 'disabled'; reason: DisabledReason};
// Reserved, not rendered in v1 — see Design decisions above:
// | { kind: 'needs_input' } | { kind: 'actioned' } | { kind: 'failed' }

export function deriveControlState(input: {
	hasEverRun: boolean;
	isRunning: boolean;
	outputRead: boolean;
	eligibility: EligibilityReason | null;
	atRunCap?: boolean;
	// Which terminal outcome a finished run reached. A failure is not a row state of its own — what
	// went wrong is legible where the conversation renders, not summarised on the task row — but it
	// is still a result the person has not seen, so the row offers it as Ready rather than hiding it.
	// A stop is the exception, and the only outcome read here: the person ended that run themselves,
	// so the control returns to Action with nothing to announce.
	outcome?: RunOutcome | null;
	// Whether the resolved agent list has anything in it (novaInvocationStore.hasAgentToRun). A bare
	// Action click runs the first agent in that list, so with an empty one there is nothing to run
	// and the click does nothing at all — no state change, no notification. That is worse than a
	// stated disabled reason in every case, and in two of them it is not even momentary: an account
	// with the flag on and no granted agents, and a run-service blip that degrades the list to `[]`,
	// both leave a live-looking control permanently dead.
	//
	// Optional, defaulting to "there is one": absent information must not disable a control, and a
	// caller that does not model the agent list at all should get the behaviour it had before.
	hasAgent?: boolean;
	// Whether the task already has a conversation on the server, from Task.hasNovaConversation.
	// hasEverRun above is session state: it is true only for a run this browser tab started and is
	// gone on the next page load, so without this a task whose conversation is sitting right there
	// offers to start a fresh one every time it is reloaded. A discarded conversation is not one -
	// the field is false for a task whose newest thread was discarded, so discarding returns the
	// control to Action, which is what discarding is for.
	//
	// Optional, defaulting to "not known": a caller that does not select the field keeps exactly the
	// behaviour it had before rather than being told there is no conversation.
	hasConversation?: boolean;
	// Whether the account's AI allowance is spent (useNovaCreditsBlock). Below every reason that is
	// about the task or person, above the run cap: a run that cannot be paid for is the truer reason
	// than one that is merely waiting for a slot. Optional, defaulting to "not blocked".
	creditsBlocked?: boolean;
}): ControlState {
	if (input.eligibility) return {kind: 'disabled', reason: input.eligibility};
	if (input.isRunning) return {kind: 'working'};
	// Checked ahead of atRunCap because the run cap's own copy tells the person to wait for a run to
	// finish — true only if a run would then be possible, which with no agent it would not be.
	const noAgentToRun = input.hasAgent === false;
	if (input.hasEverRun) {
		// A run the person stopped leaves nothing to announce: they ended it themselves, so there is
		// no result they have not already met. The override only applies here — a task that has
		// genuinely never run falls through to the run-cap check below whatever its outcome. It still
		// defers to the agent check: returning to Action with nothing to run would re-create the
		// dead click one run later.
		if (input.outcome?.kind === 'cancelled') {
			if (noAgentToRun) return {kind: 'disabled', reason: 'no_agent_available'};
			return input.creditsBlocked ? {kind: 'disabled', reason: 'out_of_credits'} : {kind: 'action'};
		}
		// A finished run stays readable whatever the list does afterwards — its answer exists, and
		// hiding it behind a disabled control because the list was re-fetched empty would lose it.
		//
		// A failed run is readable in the same way, and reads as Ready until it is opened: the thread
		// holds what was asked and what Nova said went wrong, which is the thing the person is being
		// sent to look at. Reading it is what turns this into Continue, the same as for an answer.
		return {kind: 'ready_or_continue', read: input.outputRead};
	}
	// Read as already seen rather than as a fresh result. Ready is how this control announces an
	// answer that arrived while the person was looking elsewhere; a conversation being loaded from
	// the server is one they have already met, so continuing it is the offer, not an unread badge.
	//
	// Below the run-cap and agent checks deliberately: continuing an existing conversation starts a
	// run like any other, so a person at the cap or with no agent to run must be told that here too
	// rather than being handed a control that cannot do anything.
	if (noAgentToRun) return {kind: 'disabled', reason: 'no_agent_available'};
	// Continue on a row or card only opens the task, which spends nothing, so a conversation stays
	// reachable while the allowance is spent. Not at the run cap, though: there Continue is already
	// disabled with credits to spare, and running out must not make the row more usable than that.
	if (input.creditsBlocked) {
		return input.hasConversation && !input.atRunCap
			? {kind: 'ready_or_continue', read: true}
			: {kind: 'disabled', reason: 'out_of_credits'};
	}
	if (input.atRunCap) return {kind: 'disabled', reason: 'run_cap_reached'};
	if (input.hasConversation) return {kind: 'ready_or_continue', read: true};
	return {kind: 'action'};
}

// The modal header's control starts a run from Continue as well as from Action, so a spent
// allowance disables both there. Rows and cards keep Continue, which only opens the task.
export function blockHeaderForCredits(state: ControlState, creditsBlocked: boolean): ControlState {
	if (!creditsBlocked) return state;
	if (state.kind === 'action' || state.kind === 'ready_or_continue') return {kind: 'disabled', reason: 'out_of_credits'};
	return state;
}

// The modal header's Continue, while the conversation's newest turn is an error still to be retried
// or dismissed. A conversation cannot go on from a run that never finished, so Continue waits until
// the error is dealt with - the same rule the composer applies to Reply to Nova. Rows and cards keep
// Continue: it only opens the task, which is how the person reaches the error in the first place.
export function blockHeaderForFailedTurn(state: ControlState, newestTurnFailed: boolean): ControlState {
	if (!newestTurnFailed || state.kind !== 'ready_or_continue') return state;
	return {kind: 'disabled', reason: 'last_run_failed'};
}
